import * as React from 'react';
import { useButton } from 'react-aria';
import type { AriaButtonProps } from 'react-aria';
import { ChevronLeft, ChevronRight } from '@components/atoms';
import { cn } from '@utils';

type CalendarNavButtonProps = {
	buttonProps: AriaButtonProps;
	icon: React.ReactNode;
	testId: string;
};

const CalendarNavButton: React.VoidFunctionComponent<CalendarNavButtonProps> = ({ buttonProps, icon, testId }) => {
	const ref = React.useRef<HTMLButtonElement | null>(null);
	const { buttonProps: ariaButtonProps } = useButton(buttonProps, ref);

	return (
		<button
			{...ariaButtonProps}
			ref={ref}
			data-testid={testId}
			className={cn(
				'inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-textMediumGray transition-colors outline-none',
				'hover:text-primary',
				'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
				'disabled:cursor-not-allowed disabled:text-textDisabled disabled:hover:text-textDisabled'
			)}
		>
			<span className="size-3">{icon}</span>
		</button>
	);
};

type CalendarHeaderProps = {
	title: string;
	prevButtonProps: AriaButtonProps;
	nextButtonProps: AriaButtonProps;
};

export const CalendarHeader: React.VoidFunctionComponent<CalendarHeaderProps> = ({
	title,
	prevButtonProps,
	nextButtonProps,
}) => {
	return (
		<div className="flex items-center justify-between px-1 pb-2">
			<CalendarNavButton
				buttonProps={prevButtonProps}
				icon={<ChevronLeft width="100%" height="100%" />}
				testId="calendar-date-picker-prev"
			/>
			<h2 aria-live="polite" className="text-base font-semibold text-textBlack">
				{title}
			</h2>
			<CalendarNavButton
				buttonProps={nextButtonProps}
				icon={<ChevronRight width="100%" height="100%" />}
				testId="calendar-date-picker-next"
			/>
		</div>
	);
};
